import {
  footerColumns as footerColumnsEn,
  getAppCta as getAppCtaEn,
  headerNav as headerNavEn,
  helpdeskLink,
  learnMenu as learnMenuEn,
  megaMenuColumns as megaMenuColumnsEn,
  megaMenuPromo as megaMenuPromoEn,
  mobileNav as mobileNavEn,
  primaryProducts as primaryProductsEn,
  productsLabel as productsLabelEn,
  type FooterColumn,
  type MegaMenuColumn,
} from '../navigation';
import type { LinkItem } from '../types';

/**
 * Arabic navigation labels, from sections 3 and 24 of the nsave Arabic
 * Localization Pack.
 *
 * Every entry keeps the English `href`, so Arabic navigation still points at the
 * canonical routes. `useLocalizedPath()` (see `~/lib/useLanguage`) applies the
 * `/ar` prefix at render time — but only for paths listed in `TRANSLATED_PATHS`
 * in `~/lib/language`, so adding a translated page means adding it there in the
 * same change as its `ar.*.tsx` route. Nothing in this file needs to change.
 */

/**
 * Translate a list of links by `href`, keeping hrefs and `external` flags.
 *
 * Keyed rather than positional: a positional map silently attaches the wrong
 * label — or falls back to English — the moment a link is inserted, removed or
 * reordered in `../navigation`, and both failures are invisible on the English
 * site. Keying by href makes a reorder a no-op and a new link an untranslated
 * one, which `localized.spec.ts` fails on.
 */
function relabel<T extends LinkItem>(links: T[], labels: Record<string, string>): T[] {
  return links.map((link) => ({ ...link, label: labels[link.href] ?? link.label }));
}

export const primaryProducts: LinkItem[] = relabel(primaryProductsEn, {
  '/': 'الحسابات',
  '/send-money-home': 'التحويلات',
  '/cards': 'البطاقات',
  '/rewards': 'المكافآت',
  '/invest': 'الاستثمار',
  '/stablecoin': 'العملات المستقرة',
});

export const megaMenuColumns: MegaMenuColumn[] = [
  {
    ...megaMenuColumnsEn[0],
    title: 'الأدوات',
    links: relabel(megaMenuColumnsEn[0].links, {
      '/send-money-home': 'حاسبة التحويل',
      'https://wishlist.nsave.com/': 'اقتراحاتك',
      '/compare': 'قارن الأسعار',
      '/zakat-calculator': 'حاسبة الزكاة',
      '/perks': 'المزايا والعروض',
      '/health-insurance': 'التأمين الصحي',
    }),
  },
];

/**
 * `headerNav` is the one list keyed by label rather than href: "Personal" and
 * "Accounts" both point at `/`, so href is not unique here.
 */
export const headerNav = headerNavEn.map((item) => ({
  ...item,
  label:
    ({ Personal: 'أفراد', Payroll: 'الرواتب', Pricing: 'الأسعار' } as Record<string, string>)[
      item.label
    ] ?? item.label,
}));

export const footerColumns: FooterColumn[] = [
  {
    ...footerColumnsEn[0],
    title: 'المنتجات',
    links: relabel(footerColumnsEn[0].links, {
      '/': 'الحسابات',
      '/send-money-home': 'حوّل فلوسك لبلدك',
      '/business': 'للشركات',
      '/invest': 'الاستثمار',
      '/rewards': 'المكافآت',
    }),
  },
  {
    ...footerColumnsEn[1],
    title: 'الأدوات',
    links: relabel(footerColumnsEn[1].links, {
      '/send-money-home': 'محول العملات',
      '/compare': 'قارن',
      'https://wishlist.nsave.com/': 'اقتراحاتك',
      '/zakat-calculator': 'حاسبة الزكاة',
      '/perks': 'المزايا',
    }),
  },
  {
    ...footerColumnsEn[2],
    title: 'الشركة',
    links: relabel(footerColumnsEn[2].links, {
      '/about': 'عن nsave',
      '/careers': 'الوظائف',
      '/partnerships': 'الشراكات',
      '/help': 'اتعلّم مع nsave',
      '/news': 'الأخبار',
    }),
  },
  {
    ...footerColumnsEn[3],
    title: 'قانوني',
    links: relabel(footerColumnsEn[3].links, {
      '/terms': 'الشروط والأحكام',
      '/privacy-policy': 'سياسة الخصوصية',
      '/terms/cookie': 'سياسة ملفات تعريف الارتباط',
    }),
  },
  {
    ...footerColumnsEn[4],
    title: 'الحاسبات',
    // Currency-pair labels ("USD to EGP") stay Latin per the pack's currency
    // rule; only the Arabic connector changes.
    links: footerColumnsEn[4].links.map((link) => ({
      ...link,
      label: link.label.replace(/^(\S+)\s+to\s+(\S+)$/u, 'من $1 إلى $2'),
    })),
  },
];

export const learnMenu: LinkItem[] = relabel(learnMenuEn, {
  '/help': 'المقالات',
  [helpdeskLink.href]: 'مركز المساعدة',
});

export const productsLabel: typeof productsLabelEn = 'المنتجات';

export const mobileNav: typeof mobileNavEn = {
  ...mobileNavEn,
  backLabel: 'رجوع',
  categoriesLabel: 'الأقسام',
  moreLabel: 'المزيد',
  openMenuLabel: 'افتح القائمة',
  closeMenuLabel: 'اقفل القائمة',
  directLinks: relabel(mobileNavEn.directLinks, { '/business': 'الرواتب' }),
  secondaryLinks: relabel(mobileNavEn.secondaryLinks, {
    '/pricing': 'الأسعار',
    '/help': 'اتعلّم مع nsave',
    [helpdeskLink.href]: 'مركز المساعدة',
    '/about': 'عن nsave',
  }),
};

export const getAppCta: LinkItem = { ...getAppCtaEn, label: 'نزّل التطبيق' };

export const megaMenuPromo = {
  ...megaMenuPromoEn,
  label: 'عن nsave',
  image: { ...megaMenuPromoEn.image, alt: 'ناس بتستخدم nsave مع بعض' },
};
